import { useMemo, useState, useRef, useCallback } from "react";
import { RotateCcw, Move } from "lucide-react";

const PVC_COLORS = {
  white: "#F5F5F5",
  black: "#1F1F1F",
};

const ACRYLIC_COLOR = "rgba(220, 235, 255, 0.25)";
const HARDWARE_BLACK = "#1a1a1a";

const PVCEnclosureViewer = ({ config }) => {
  const { width, height, depth, pvc_color } = config;
  const colorHex = PVC_COLORS[pvc_color] || PVC_COLORS.white;
  
  const [rotationY, setRotationY] = useState(45);
  const [rotationX, setRotationX] = useState(25);
  const [isDragging, setIsDragging] = useState(false);
  const lastMousePos = useRef({ x: 0, y: 0 });
  
  const useDoubleDoors = width > 4 || height > 4 || depth > 4;
  
  const svgWidth = 400;
  const svgHeight = 300;
  const maxDimension = Math.max(width, height, depth);
  const scale = (svgWidth - 80) / (maxDimension * 1.8);
  
  const rotYRad = (rotationY * Math.PI) / 180;
  const rotXRad = (rotationX * Math.PI) / 180;
  
  const project3D = useCallback((x, y, z) => {
    const cosY = Math.cos(rotYRad);
    const sinY = Math.sin(rotYRad);
    const x1 = x * cosY - z * sinY;
    const z1 = x * sinY + z * cosY;
    
    const cosX = Math.cos(rotXRad);
    const sinX = Math.sin(rotXRad);
    const y1 = y * cosX - z1 * sinX;
    const z2 = y * sinX + z1 * cosX;
    
    const perspective = 800;
    const projectionScale = perspective / (perspective + z2);
    
    return {
      x: svgWidth / 2 + x1 * scale * projectionScale,
      y: svgHeight / 2 - y1 * scale * projectionScale,
      z: z2
    };
  }, [rotYRad, rotXRad, scale]);
  
  const handleMouseDown = (e) => {
    setIsDragging(true);
    lastMousePos.current = { x: e.clientX, y: e.clientY };
  };
  
  const handleMouseMove = useCallback((e) => {
    if (!isDragging) return;
    const deltaX = e.clientX - lastMousePos.current.x;
    const deltaY = e.clientY - lastMousePos.current.y;
    setRotationY(prev => (prev + deltaX * 0.5) % 360);
    setRotationX(prev => Math.max(-60, Math.min(60, prev - deltaY * 0.3)));
    lastMousePos.current = { x: e.clientX, y: e.clientY };
  }, [isDragging]);
  
  const handleMouseUp = () => setIsDragging(false);
  
  const handleTouchStart = (e) => {
    if (e.touches.length === 1) {
      setIsDragging(true);
      lastMousePos.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
    }
  };
  
  const handleTouchMove = useCallback((e) => {
    if (!isDragging || e.touches.length !== 1) return;
    const deltaX = e.touches[0].clientX - lastMousePos.current.x;
    const deltaY = e.touches[0].clientY - lastMousePos.current.y;
    setRotationY(prev => (prev + deltaX * 0.5) % 360);
    setRotationX(prev => Math.max(-60, Math.min(60, prev - deltaY * 0.3)));
    lastMousePos.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
  }, [isDragging]);
  
  const resetRotation = () => {
    setRotationY(45);
    setRotationX(25);
  };
  
  const pointsString = (vertices) => vertices.map(v => `${v.x},${v.y}`).join(' ');
  
  const darkerShade = (hex, amount) => {
    const num = parseInt(hex.slice(1), 16);
    const r = Math.max(0, (num >> 16) - amount);
    const g = Math.max(0, ((num >> 8) & 0x00FF) - amount);
    const b = Math.max(0, (num & 0x0000FF) - amount);
    return `rgb(${r},${g},${b})`;
  };

  // Compute face normal to determine visibility (backface culling)
  const isFaceVisible = (verts) => {
    const v1 = { x: verts[1].x - verts[0].x, y: verts[1].y - verts[0].y };
    const v2 = { x: verts[2].x - verts[0].x, y: verts[2].y - verts[0].y };
    return (v1.x * v2.y - v1.y * v2.x) > 0;
  };

  const renderEnclosure = useMemo(() => {
    const w = width / 2;
    const h = height / 2;
    const d = depth / 2;
    
    // 8 corners of the box
    const corners = {
      bbl: project3D(-w, -h, -d), // back-bottom-left
      bbr: project3D(w, -h, -d),  // back-bottom-right
      btl: project3D(-w, h, -d),  // back-top-left
      btr: project3D(w, h, -d),   // back-top-right
      fbl: project3D(-w, -h, d),  // front-bottom-left
      fbr: project3D(w, -h, d),   // front-bottom-right
      ftl: project3D(-w, h, d),   // front-top-left
      ftr: project3D(w, h, d),    // front-top-right
    };
    
    // Vent strip at top rear - a thin strip along the back-top edge
    const ventHeight = 0.15; // ~45mm
    const ventCorners = {
      bl: project3D(-w, h - ventHeight, -d),
      br: project3D(w, h - ventHeight, -d),
      tl: corners.btl,
      tr: corners.btr,
    };
    
    // Frame gap for door cutout (100mm = 0.328ft)
    const frameGap = 0.328;
    const fw = frameGap;
    const fh = frameGap;
    
    // Door frame inner corners
    const doorInner = {
      bl: project3D(-w + fw, -h + fh, d),
      br: project3D(w - fw, -h + fh, d),
      tl: project3D(-w + fw, h - fh, d),
      tr: project3D(w - fw, h - fh, d),
    };
    
    // Double door geometry
    const dividerWidth = 0.15;
    const leftDoorInner = {
      bl: project3D(-w + fw, -h + fh, d),
      br: project3D(-dividerWidth - fw/2, -h + fh, d),
      tl: project3D(-w + fw, h - fh, d),
      tr: project3D(-dividerWidth - fw/2, h - fh, d),
    };
    const rightDoorInner = {
      bl: project3D(dividerWidth + fw/2, -h + fh, d),
      br: project3D(w - fw, -h + fh, d),
      tl: project3D(dividerWidth + fw/2, h - fh, d),
      tr: project3D(w - fw, h - fh, d),
    };
    const dividerCorners = {
      bl: project3D(-dividerWidth, -h, d),
      br: project3D(dividerWidth, -h, d),
      tl: project3D(-dividerWidth, h, d),
      tr: project3D(dividerWidth, h, d),
    };
    
    // Hinge positions as 3D projected points
    const hingeWidth = 0.12;
    const hingeHeight = 0.35;
    
    const makeHinge3D = (xPos, yCenter, zPos) => {
      return {
        bl: project3D(xPos, yCenter - hingeHeight/2, zPos),
        br: project3D(xPos + hingeWidth, yCenter - hingeHeight/2, zPos),
        tr: project3D(xPos + hingeWidth, yCenter + hingeHeight/2, zPos),
        tl: project3D(xPos, yCenter + hingeHeight/2, zPos),
      };
    };
    
    // Handle as 3D projected quad
    const makeHandle3D = (xPos, yCenter, zPos) => {
      const hw = 0.15;
      const hh = 0.35;
      return {
        bl: project3D(xPos - hw/2, yCenter - hh/2, zPos),
        br: project3D(xPos + hw/2, yCenter - hh/2, zPos),
        tr: project3D(xPos + hw/2, yCenter + hh/2, zPos),
        tl: project3D(xPos - hw/2, yCenter + hh/2, zPos),
      };
    };
    
    // Corner bracket as 3D projected L-shape
    const makeBracket3D = (cx, cy, zPos, flipX, flipY) => {
      const bSize = 0.25;
      const bThick = 0.08;
      const dx = flipX ? -1 : 1;
      const dy = flipY ? -1 : 1;
      return [
        project3D(cx, cy, zPos),
        project3D(cx + dx * bSize, cy, zPos),
        project3D(cx + dx * bSize, cy + dy * bThick, zPos),
        project3D(cx + dx * bThick, cy + dy * bThick, zPos),
        project3D(cx + dx * bThick, cy + dy * bSize, zPos),
        project3D(cx, cy + dy * bSize, zPos),
      ];
    };

    // Define all 6 faces with winding order for backface culling
    const allFaces = [
      { name: 'back', verts: [corners.bbl, corners.bbr, corners.btr, corners.btl], shade: 0 },
      { name: 'front', verts: [corners.fbr, corners.fbl, corners.ftl, corners.ftr], shade: 0, isFront: true },
      { name: 'left', verts: [corners.fbl, corners.bbl, corners.btl, corners.ftl], shade: 15 },
      { name: 'right', verts: [corners.bbr, corners.fbr, corners.ftr, corners.btr], shade: 10 },
      { name: 'top', verts: [corners.btl, corners.btr, corners.ftr, corners.ftl], shade: 5 },
      { name: 'bottom', verts: [corners.fbl, corners.fbr, corners.bbr, corners.bbl], shade: 20 },
    ];
    
    // Filter visible faces, sort by depth (painter's algorithm)
    const visibleFaces = allFaces
      .map(face => {
        const avgZ = face.verts.reduce((sum, v) => sum + v.z, 0) / face.verts.length;
        const visible = isFaceVisible(face.verts);
        return { ...face, avgZ, visible };
      })
      .filter(f => f.visible)
      .sort((a, b) => b.avgZ - a.avgZ);
    
    // Check if back face is visible (for vent strip)
    const backFaceVisible = isFaceVisible([corners.bbl, corners.bbr, corners.btr, corners.btl]);
    const ventFaceVerts = [ventCorners.bl, ventCorners.br, ventCorners.tr, ventCorners.tl];
    const ventVisible = backFaceVisible;
    
    // Check if front face is visible (for door hardware)
    const frontFaceVisible = isFaceVisible([corners.fbr, corners.fbl, corners.ftl, corners.ftr]);
    
    // Vent slot positions along the strip
    const ventSlotCount = 8;
    const ventSlots = [];
    for (let i = 0; i < ventSlotCount; i++) {
      const t1 = (i + 0.3) / ventSlotCount;
      const t2 = (i + 0.7) / ventSlotCount;
      const slotGapY = 0.03;
      ventSlots.push([
        project3D(-w + (2*w) * t1, h - slotGapY, -d),
        project3D(-w + (2*w) * t2, h - slotGapY, -d),
        project3D(-w + (2*w) * t2, h - ventHeight + slotGapY, -d),
        project3D(-w + (2*w) * t1, h - ventHeight + slotGapY, -d),
      ]);
    }

    // Single door hinges (left side of front face)
    const singleHinges = [
      makeHinge3D(-w, -h * 0.5, d + 0.01),
      makeHinge3D(-w, 0, d + 0.01),
      makeHinge3D(-w, h * 0.5, d + 0.01),
    ];
    
    // Single door handle (right side)
    const singleHandle = makeHandle3D(w - fw * 1.5, 0, d + 0.01);
    
    // Single door brackets
    const singleBrackets = [
      makeBracket3D(-w + fw, h - fh, d + 0.01, false, true),  // top-left
      makeBracket3D(w - fw, h - fh, d + 0.01, true, true),    // top-right
      makeBracket3D(-w + fw, -h + fh, d + 0.01, false, false), // bottom-left
      makeBracket3D(w - fw, -h + fh, d + 0.01, true, false),   // bottom-right
    ];
    
    // Double door hinges
    const doubleLeftHinges = [
      makeHinge3D(-w, -h * 0.5, d + 0.01),
      makeHinge3D(-w, 0, d + 0.01),
      makeHinge3D(-w, h * 0.5, d + 0.01),
    ];
    const doubleRightHinges = [
      makeHinge3D(w - hingeWidth, -h * 0.5, d + 0.01),
      makeHinge3D(w - hingeWidth, 0, d + 0.01),
      makeHinge3D(w - hingeWidth, h * 0.5, d + 0.01),
    ];
    
    // Double door handles
    const doubleLeftHandle = makeHandle3D(-dividerWidth - fw, 0, d + 0.01);
    const doubleRightHandle = makeHandle3D(dividerWidth + fw, 0, d + 0.01);
    
    // Double door brackets
    const doubleBrackets = [
      // Left door
      makeBracket3D(-w + fw, h - fh, d + 0.01, false, true),
      makeBracket3D(-dividerWidth - fw/2, h - fh, d + 0.01, true, true),
      makeBracket3D(-w + fw, -h + fh, d + 0.01, false, false),
      makeBracket3D(-dividerWidth - fw/2, -h + fh, d + 0.01, true, false),
      // Right door
      makeBracket3D(dividerWidth + fw/2, h - fh, d + 0.01, false, true),
      makeBracket3D(w - fw, h - fh, d + 0.01, true, true),
      makeBracket3D(dividerWidth + fw/2, -h + fh, d + 0.01, false, false),
      makeBracket3D(w - fw, -h + fh, d + 0.01, true, false),
    ];

    return (
      <g>
        {/* Render solid panels in depth order */}
        {visibleFaces.map((face) => {
          if (face.isFront) {
            // Front face: PVC frame with acrylic door cutout
            return (
              <g key={face.name}>
                {/* Solid PVC front panel (frame) */}
                <polygon
                  points={pointsString(face.verts)}
                  fill={darkerShade(colorHex, face.shade)}
                  stroke={darkerShade(colorHex, 30)}
                  strokeWidth="1.5"
                />
                
                {useDoubleDoors ? (
                  <>
                    {/* Left acrylic window */}
                    <polygon
                      points={pointsString([leftDoorInner.bl, leftDoorInner.br, leftDoorInner.tr, leftDoorInner.tl])}
                      fill={ACRYLIC_COLOR}
                      stroke="rgba(180,210,255,0.4)"
                      strokeWidth="1"
                    />
                    {/* Center divider strip */}
                    <polygon
                      points={pointsString([dividerCorners.bl, dividerCorners.br, dividerCorners.tr, dividerCorners.tl])}
                      fill={colorHex}
                      stroke={darkerShade(colorHex, 30)}
                      strokeWidth="1"
                    />
                    {/* Right acrylic window */}
                    <polygon
                      points={pointsString([rightDoorInner.bl, rightDoorInner.br, rightDoorInner.tr, rightDoorInner.tl])}
                      fill={ACRYLIC_COLOR}
                      stroke="rgba(180,210,255,0.4)"
                      strokeWidth="1"
                    />
                    {/* Corner brackets */}
                    {doubleBrackets.map((b, i) => (
                      <polygon key={`db-${i}`} points={pointsString(b)} fill={HARDWARE_BLACK} />
                    ))}
                    {/* Left door hinges */}
                    {doubleLeftHinges.map((h, i) => (
                      <polygon key={`dlh-${i}`} points={pointsString([h.bl, h.br, h.tr, h.tl])} fill={HARDWARE_BLACK} />
                    ))}
                    {/* Right door hinges */}
                    {doubleRightHinges.map((h, i) => (
                      <polygon key={`drh-${i}`} points={pointsString([h.bl, h.br, h.tr, h.tl])} fill={HARDWARE_BLACK} />
                    ))}
                    {/* Door handles */}
                    <polygon points={pointsString([doubleLeftHandle.bl, doubleLeftHandle.br, doubleLeftHandle.tr, doubleLeftHandle.tl])} fill={HARDWARE_BLACK} />
                    <polygon points={pointsString([doubleRightHandle.bl, doubleRightHandle.br, doubleRightHandle.tr, doubleRightHandle.tl])} fill={HARDWARE_BLACK} />
                  </>
                ) : (
                  <>
                    {/* Single acrylic window */}
                    <polygon
                      points={pointsString([doorInner.bl, doorInner.br, doorInner.tr, doorInner.tl])}
                      fill={ACRYLIC_COLOR}
                      stroke="rgba(180,210,255,0.4)"
                      strokeWidth="1"
                    />
                    {/* Corner brackets */}
                    {singleBrackets.map((b, i) => (
                      <polygon key={`sb-${i}`} points={pointsString(b)} fill={HARDWARE_BLACK} />
                    ))}
                    {/* Hinges on left side */}
                    {singleHinges.map((h, i) => (
                      <polygon key={`sh-${i}`} points={pointsString([h.bl, h.br, h.tr, h.tl])} fill={HARDWARE_BLACK} />
                    ))}
                    {/* Door handle on right */}
                    <polygon points={pointsString([singleHandle.bl, singleHandle.br, singleHandle.tr, singleHandle.tl])} fill={HARDWARE_BLACK} />
                  </>
                )}
              </g>
            );
          }
          
          // Regular solid PVC panel
          return (
            <g key={face.name}>
              <polygon
                points={pointsString(face.verts)}
                fill={darkerShade(colorHex, face.shade)}
                stroke={darkerShade(colorHex, 30)}
                strokeWidth="1.5"
              />
            </g>
          );
        })}
        
        {/* Ventilation strip at top rear - rendered as 3D polygon */}
        {ventVisible && (
          <g>
            <polygon
              points={pointsString(ventFaceVerts)}
              fill={HARDWARE_BLACK}
              stroke="#333"
              strokeWidth="0.5"
            />
            {/* Vent slots */}
            {ventSlots.map((slot, i) => (
              <polygon
                key={`vs-${i}`}
                points={pointsString(slot)}
                fill="#0a0a0a"
              />
            ))}
          </g>
        )}
      </g>
    );
  }, [width, height, depth, colorHex, useDoubleDoors, project3D]);

  return (
    <div 
      className="w-full h-full flex items-center justify-center bg-[#0A0A0A] relative select-none" 
      data-testid="pvc-enclosure-viewer"
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleMouseUp}
      style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
    >
      <svg
        viewBox={`0 0 ${svgWidth} ${svgHeight}`}
        className="w-full h-full max-w-[600px] max-h-[400px]"
        style={{ filter: 'drop-shadow(0 0 20px rgba(100, 150, 255, 0.15))' }}
      >
        {/* Background grid */}
        <g opacity="0.1">
          {[...Array(10)].map((_, i) => (
            <line key={`h${i}`} x1="0" y1={i * 30 + 15} x2={svgWidth} y2={i * 30 + 15} stroke="#6B8DD6" strokeWidth="0.5" />
          ))}
          {[...Array(14)].map((_, i) => (
            <line key={`v${i}`} x1={i * 30 + 10} y1="0" x2={i * 30 + 10} y2={svgHeight} stroke="#6B8DD6" strokeWidth="0.5" />
          ))}
        </g>
        
        {renderEnclosure}
        
        {/* Dimension label */}
        <g fill="#F4F1DE" fontSize="12" fontFamily="Space Grotesk, sans-serif">
          <text x={svgWidth / 2} y={svgHeight - 10} textAnchor="middle" opacity="0.6">
            {width}ft W x {height}ft H x {depth}ft D
          </text>
        </g>
      </svg>
      
      {/* Controls */}
      <div className="absolute bottom-4 left-4 flex items-center gap-3">
        <div className="flex items-center gap-2 text-[#F4F1DE]/50 text-xs bg-[#121212]/80 backdrop-blur px-3 py-2 rounded border border-white/10">
          <Move className="w-3 h-3" />
          <span>Drag to rotate</span>
        </div>
        <button
          onClick={resetRotation}
          className="flex items-center gap-1.5 text-[#F4F1DE]/60 hover:text-blue-400 text-xs bg-[#121212]/80 backdrop-blur px-3 py-2 rounded border border-white/10 hover:border-blue-400/50 transition-colors"
        >
          <RotateCcw className="w-3 h-3" />
          <span>Reset</span>
        </button>
      </div>
      
      {/* Door type indicator */}
      <div className="absolute top-4 left-4 bg-blue-500/20 text-blue-400 text-xs px-3 py-1.5 rounded-full">
        {useDoubleDoors ? '2 Hinged Doors' : '1 Hinged Door'}
      </div>
      
      {/* Rotation indicator */}
      <div className="absolute top-4 right-4 text-[#F4F1DE]/30 text-xs bg-[#121212]/60 backdrop-blur px-2 py-1 rounded">
        {Math.round(rotationY)}° / {Math.round(rotationX)}°
      </div>
    </div>
  );
};

export default PVCEnclosureViewer;
